import { FormikProps } from "formik";
import React, { memo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import { Option } from "types/types";
import * as Yup from "yup";

import { Button } from "@/components/Button";
import { FormikError, VALIDATION_TYPES } from "@/components/Formik";
import { Header } from "@/components/Header";
import { Icon } from "@/components/Icon";
import { LoadingSpinner } from "@/components/Loading";
import { Text } from "@/components/Text";
import { MEDIA_SIZE_SM } from "@/core/media";
import { IConsultantProfileRole, IRole } from "@/data/roles";
import { Flex, Group } from "@/eworkitComponents";
import { ICodelistGenericSelectParams } from "@/views/components/Codelists";
import { FormikCodelistGenericSelect } from "@/views/components/Formik";
import { FormikRoleCreatableTypeahead } from "@/views/components/RoleCreatableTypeahead";
import { SuggestedRolesList } from "@/views/components/Roles";
import { POSITION_MAX_CHARACTERS } from "@/views/ConsultantProfiles/Detail/GeneralSection/GeneralSectionEdit";

import { SuggestedSection } from "./SuggestedSection";

const ROLE_LEVELS_CODELIST_QUERY: ICodelistGenericSelectParams = { codelistName: "skill-role-levels" };

export interface IProfileRoleSectionValues {
  roles: IConsultantProfileRole[];
}

export const profileRoleSectionValidationSchema = () =>
  Yup.object()
    .required()
    .shape({
      position: Yup.object().shape({
        name: Yup.string().max(POSITION_MAX_CHARACTERS, VALIDATION_TYPES.MAX_LENGTH),
      }),
      roles: Yup.array().of(
        Yup.object().shape({
          role: Yup.object()
            .required(VALIDATION_TYPES.REQUIRED)
            .nullable()
            .shape({
              name: Yup.string().required(VALIDATION_TYPES.REQUIRED).nullable(),
            }),
          roleLevel: Yup.string().nullable(),
        }),
      ),
    });

interface IProfileRoleSectionProps {
  form: FormikProps<IProfileRoleSectionValues>;
  suggestedRoles?: IRole[];
}

export const ProfileRoleSection = memo((props: IProfileRoleSectionProps) => {
  const { t } = useTranslation("session");
  const { form } = props;

  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const addRole = useCallback(
    (role: Partial<IConsultantProfileRole>) => {
      form.setFieldValue("roles", [...form.values.roles, role]);
    },
    [props.form],
  );

  const removeRole = useCallback(
    (role: IRole) => {
      if (form.values.roles.length === 1 && form.values.roles[0].role?.id === role.id) {
        form.setFieldValue("roles", [{}]);
      } else {
        form.setFieldValue("roles", [...form.values.roles.filter(l => l.role?.id !== role?.id)]);
      }
    },
    [form],
  );

  const handleSuggestedRoleClick = useCallback(
    (role: IRole) => {
      const emptyRoleIndex = form.values.roles.findIndex(r => r.role === undefined);
      if (emptyRoleIndex > -1) {
        form.setFieldValue("roles", [
          ...form.values.roles.slice(0, emptyRoleIndex),
          { role },
          ...form.values.roles.slice(emptyRoleIndex + 1),
        ]);
        return;
      }

      addRole({ role });
    },
    [form],
  );

  const handleCheckedSuggestedRoleClick = useCallback(
    (role: IRole) => {
      removeRole(role);
    },
    [removeRole],
  );

  const handleDeleteRoleClick = useCallback(
    (index: number) => {
      form.setFieldValue("roles", [...form.values.roles.slice(0, index), ...form.values.roles.slice(index + 1)]);
    },
    [form],
  );

  const handleAddNextClick = useCallback(() => {
    addRole({ role: null as any });
  }, [addRole]);

  const filterOption = (option: Option<IRole>, _: string) =>
    !form.values.roles.find((r: IConsultantProfileRole) => r.role && r.role.id === option.data.id);

  return (
    <Flex direction="column" innerMargin="sm">
      <Header size="sm" color="black" weight="bold">
        {t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.HEADER_ROLE")}
      </Header>

      <Flex width={12} grow={1} direction="column" innerMargin="md">
        <Flex width={12} direction="column" innerMargin="md">
          {form.values.roles.map((role, roleIndex) => (
            <Flex key={role.role?.name || roleIndex} direction={isSmallScreen ? "column" : "row"} align="flex-end">
              <Flex
                width={12}
                grow={1}
                innerMargin={isSmallScreen ? "md" : "xl"}
                direction={isSmallScreen ? "column" : "row"}
                align="flex-start">
                <Group
                  direction="column"
                  label={t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.LABEL_ROLE")}
                  width={12}>
                  <FormikRoleCreatableTypeahead
                    value={role.role}
                    name={`roles[${roleIndex}].role`}
                    testName="role"
                    valueKey="id"
                    filterOption={filterOption}
                    labelKey="name"
                    size="md"
                    placeholder={t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.PLACEHOLDER_ROLE")}
                    form={props.form}
                  />
                </Group>
                <Group
                  direction="column"
                  label={t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.LABEL_SENIORITY")}
                  width={12}>
                  <Flex>
                    <Flex width={12} direction="column">
                      <FormikCodelistGenericSelect
                        simpleValue
                        value={role.roleLevel}
                        name={`roles[${roleIndex}].roleLevel`}
                        testName="role-level"
                        query={ROLE_LEVELS_CODELIST_QUERY}
                        size="md"
                        filterOption={filterOption}
                        placeholder={t(
                          "CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.PLACEHOLDER_LANGUAGE_LEVEL",
                        )}
                        isClearable={false}
                      />
                    </Flex>
                    <Button variant="clean" onClick={() => handleDeleteRoleClick(roleIndex)}>
                      <Icon color="grey2" icon="close" size="lg" />
                    </Button>
                  </Flex>
                </Group>
              </Flex>
              <FormikError name={`roles[${roleIndex}].roleLevel`} data-test="error-role-level" />
            </Flex>
          ))}
          <Flex>
            <Button variant="secondary" display="inline-block" onClick={handleAddNextClick}>
              <Text>{t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.BUTTON_ADD_NEXT_ROLE")}</Text>
            </Button>
          </Flex>

          {props.suggestedRoles ? (
            <SuggestedSection header={t("CONSULTANT_PROFILES.DETAIL.SUGGESTED_DATA.PROFILE_FORM.SUGGESTED_ROLES")}>
              <SuggestedRolesList
                onClick={handleSuggestedRoleClick}
                onCheckedClick={handleCheckedSuggestedRoleClick}
                suggestedRoles={props.suggestedRoles}
                selectedRoles={form.values.roles.map(r => r.role)}
              />
            </SuggestedSection>
          ) : (
            <Flex align="center" justify="center" grow={1} padding={["lg"]}>
              <LoadingSpinner size="lg" variant="primary" />
            </Flex>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});
